import {
  RadixTooltip,
  RadixTooltipContent,
  RadixTooltipProvider,
  RadixTooltipTrigger,
  TooltipSubText,
  TooltipText,
} from '../../../../components/ui/tooltip';
import { cn } from '../../../../lib/utils';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

export type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';
export type TooltipType = 'default' | 'withButton';

export interface TooltipProps {
  /** 툴팁 타입 */
  type?: TooltipType;
  /** 툴팁 위치 */
  side?: TooltipPosition;
  /** 툴팁 화살표 위치 오프셋 */
  sideOffset?: number;
  /** 툴팁 기본 열림 여부 */
  defaultOpen?: boolean | undefined;
  /** 툴팁 열림 여부 */
  open?: boolean | undefined;
  /** 툴팁 열림 여부 변경 시 실행되는 함수 */
  onOpenChange?: (open: boolean) => void | undefined;
  /** 툴팁 메인 텍스트 또는 ReactNode */
  textNode: React.ReactNode;
  /** 툴팁 메인 텍스트 Props */
  textProps?: React.ComponentProps<typeof TooltipText>;
  /** 툴팁 서브 텍스트 또는 ReactNode */
  subTextNode?: React.ReactNode;
  /** 툴팁 서브 텍스트 Props */
  subTextProps?: React.ComponentProps<typeof TooltipSubText>;
  /** withButton 타입에서 텍스트 아래에 배치될 버튼 (xsmall GlobalButton 등) */
  buttonNode?: React.ReactNode;
  /** withButton 타입 버튼 Wrapper Props */
  buttonWrapperProps?: React.ComponentProps<'div'>;
  /** 툴팁 지연 시간 */
  delayDuration?: number;
  /** 툴팁 자식 요소 */
  children: React.ReactNode;
  /** 툴팁 클래스명 */
  className?: string;
  /** 툴팁 Provider Props */
  providerProps?: React.ComponentProps<typeof TooltipPrimitive.Provider>;
  /** 툴팁 Root Props */
  tooltipProps?: React.ComponentProps<typeof TooltipPrimitive.Root>;
  /** 툴팁 Trigger Props */
  triggerProps?: React.ComponentProps<typeof TooltipPrimitive.Trigger>;
}

export default function Tooltip({
  type = 'default',
  textNode,
  textProps,
  subTextNode,
  subTextProps,
  buttonNode,
  buttonWrapperProps,
  side = 'top',
  sideOffset = 0,
  delayDuration = 200,
  defaultOpen,
  open,
  onOpenChange,
  className,
  children,
  providerProps,
  tooltipProps,
  triggerProps,
  ...props
}: TooltipProps) {
  return (
    <RadixTooltipProvider {...providerProps}>
      <RadixTooltip
        delayDuration={delayDuration}
        {...tooltipProps}
        {...(defaultOpen !== undefined && { defaultOpen })}
        {...(open !== undefined && { open })}
        {...(onOpenChange !== undefined && { onOpenChange })}
      >
        {/* @FIXME 이놈이 button이라서 textNode로 Button을 넣으면 안되는 리펙토링 필요 */}
        <RadixTooltipTrigger {...triggerProps}>{children}</RadixTooltipTrigger>
        <RadixTooltipContent
          side={side}
          className={cn(
            type === 'withButton' && 'flex-col items-start',
            className,
          )}
          sideOffset={sideOffset}
          {...props}
        >
          <div className="flex items-center gap-5xs">
            {textNode && <TooltipText {...textProps}>{textNode}</TooltipText>}
            {subTextNode && (
              <TooltipSubText {...subTextProps}>{subTextNode}</TooltipSubText>
            )}
          </div>
          {type === 'withButton' && buttonNode && (
            <div
              {...buttonWrapperProps}
              className={cn(
                'relative z-10 w-full',
                buttonWrapperProps?.className,
              )}
            >
              {buttonNode}
            </div>
          )}
        </RadixTooltipContent>
      </RadixTooltip>
    </RadixTooltipProvider>
  );
}
